// =============================================================================
// mopBackbone.ts — read a "MOP backbone" markdown file straight into proposals
// =============================================================================
// James, 21 Sep 2026: "I work with claude projects on most, he is the best
// person to provide a MOP MD summary file to be built around with a worker and
// with the brain we can keep it live and up to date." The Projects Claude
// writes the backbone section by section against the builder's own fields;
// this module maps it deterministically — no AI, no guessing — into the same
// proposal shape the extract review modal already shows, so every block still
// lands only when the manager ticks it.
//
// Headings are matched by keyword, not exact text, so both the strict
// template (brain: "MOP backbone - the file the Projects Claude writes for
// the builder") and the first Willowvale backbone ("## BUILDER §4 · SCOPE &
// STANDING CONTROLS", "### Site notes — paste after the fixed wording") parse.
//
// [OPEN] / [VERIFY: …] tags are never promoted into a value: a wind limit
// tagged open stays empty and the tag goes to "to confirm"; a step keeps the
// tag text so the reader sees it. That is the backbone's own rule 2.
// =============================================================================

export interface BackboneStep { title: string; action: string; details: string; source: string; hold?: boolean }
export interface BackboneProposals {
  backbone: true;
  title?: string;
  work_date?: { value: string; source: string };            // yyyy-mm-dd
  client_chain?: { value: string; source: string };
  crew: { name_role: string; source: string }[];
  crane_none?: { value: true; source: string };
  plant: { item: string; details: string; source: string }[];
  scope?: { value: string; source: string };
  suspended_loads?: { value: boolean; source: string };
  painted_structure?: { value: boolean; source: string };
  site_notes?: { value: string; source: string };
  wind_limit_ms?: { value: number; source: string };
  environmental_notes?: { value: string; source: string };
  steps: BackboneStep[];
  // Projects brief 2, A2 — a titled section that governs every step without
  // being one (the call protocol, the rig description). It used to be dropped
  // because the builder had no field for it.
  blocks: { title: string; body: string; source: string }[];
  replace_steps: boolean;
  donts: { text: string; source: string }[];
  to_confirm: { note: string; source: string }[];
}

interface Section { level: number; heading: string; key: string; lines: string[] }

const clean = (s: string) =>
  s.replace(/\*\*/g, "").replace(/`/g, "").replace(/^\s*>\s?/, "").trimEnd();
const isTag = (s: string) => /\[(OPEN|VERIFY)[^\]]*\]/i.test(s);
const tagsIn = (s: string) => (s.match(/\[(OPEN|VERIFY)[^\]]*\]/gi) ?? []).map(t => t.replace(/^\[|\]$/g, "").trim());

function splitSections(md: string): Section[] {
  const out: Section[] = [];
  let cur: Section = { level: 0, heading: "", key: "", lines: [] };
  for (const raw of md.split(/\r?\n/)) {
    const m = raw.match(/^(#{1,6})\s+(.*)$/);
    if (m) {
      out.push(cur);
      const heading = m[2].trim();
      cur = { level: m[1].length, heading, key: heading.toLowerCase(), lines: [] };
    } else {
      cur.lines.push(raw);
    }
  }
  out.push(cur);
  return out;
}

// Paragraph text of a section: blockquotes unwrapped, bold stripped, helper
// lines ("paste this", separators, tables) dropped.
function proseOf(sec: Section): string {
  const paras: string[] = [];
  let buf: string[] = [];
  const flush = () => { if (buf.length) { paras.push(buf.join(" ").trim()); buf = []; } };
  for (const raw of sec.lines) {
    const l = clean(raw);
    if (!l.trim()) { flush(); continue; }
    if (/^-{3,}$/.test(l.trim()) || l.trim().startsWith("|")) { flush(); continue; }
    if (/^\*?paste (this|after)/i.test(l.trim())) continue;
    buf.push(l.trim());
  }
  flush();
  return paras.join("\n\n").trim();
}

// The body of a document block, kept close to the page it came from. Unlike
// proseOf, lines are NOT joined into paragraphs: a call protocol is a numbered
// list and a call-and-read-back example, and running those together makes it
// unreadable at an anchor. Instructions addressed to the builder rather than to
// the crew are dropped.
function blockBodyOf(sec: Section): string {
  const out: string[] = [];
  // 23 Sep 2026 — an instruction addressed to the builder runs on past its own
  // first line ("BUILDER INSTRUCTION: this block must PRINT IN THE MOP …" and
  // then "The method steps refer to … they must never refer to a section
  // number"), and that second line printed on the MOP as though it were a rule
  // for the crew. The whole paragraph goes, down to the next blank line.
  let skippingInstruction = false;
  for (const raw of sec.lines) {
    const l = clean(raw).trim();
    if (/builder instruction/i.test(l)) { skippingInstruction = true; continue; }
    if (skippingInstruction) { if (!l) skippingInstruction = false; continue; }
    if (/^-{3,}$/.test(l)) continue;
    if (/^\|[\s:|-]+\|$/.test(l)) continue;                 // the |---|---| rule
    if (l.startsWith("|")) { out.push(l); continue; }        // a table row, kept so the PDF can rebuild it
    if (!l) { if (out.length && out[out.length - 1] !== "") out.push(""); continue; }
    out.push(l.replace(/\*/g, ""));
  }
  while (out.length && out[out.length - 1] === "") out.pop();
  return out.join("\n").trim();
}

// A section can hold more than one table — §3 of the input-notes shape holds
// the rigging table and the instruments table under the same heading. So the
// header row is found by the |---|---| rule that follows it, not by assuming
// the first row of the section is the only header.
function tableRows(sec: Section): string[][] {
  const rows: string[][] = [];
  for (const raw of sec.lines) {
    const l = raw.trim();
    if (!l.startsWith("|")) continue;
    if (/^\|[\s:|-]+\|$/.test(l)) { rows.pop(); continue; }   // the row before the rule was a header
    rows.push(l.split("|").slice(1, -1).map(c => clean(c).trim()));
  }
  return rows;
}

function bullets(sec: Section): string[] {
  return sec.lines.map(clean).map(l => l.trim()).filter(l => /^[-*•]\s+/.test(l)).map(l => l.replace(/^[-*•]\s+/, "").trim());
}

// Several headings can match one keyword ("## SCOPE & STANDING CONTROLS" and
// its "### Standing control checkboxes" child) — take the one that has content.
function findSec(secs: Section[], test: (key: string) => boolean): Section | undefined {
  const hits = secs.filter(s => s.key && test(s.key));
  if (hits.length <= 1) return hits[0];
  const filled = (s: Section) => s.lines.filter(l => l.trim()).length;
  return hits.slice().sort((a, b) => filled(b) - filled(a))[0];
}

// A section plus every heading nested under it, as one block.
function mergeChildren(secs: Section[], parent: Section): Section {
  const idx = secs.indexOf(parent);
  const merged: Section = { ...parent, lines: [...parent.lines] };
  for (let i = idx + 1; i < secs.length; i++) {
    if (secs[i].level > parent.level) merged.lines.push(`**${secs[i].heading}**`, ...secs[i].lines);
    else break;
  }
  return merged;
}

// Two spellings of the same block must not both land on the MOP. "Temporary
// rig" and "The temporary rig" came out of two builds of the reader and the
// MOP ended up carrying both (James, 23 Sep: "the Temporary Rig and call
// protocol seem to be doubled up").
export function blockKey(title: string): string {
  return (title ?? "").toLowerCase().replace(/^the\s+/, "").replace(/[^a-z0-9]+/g, "");
}

// "5. Communications protocol — THE CALL PROTOCOL" → "Call protocol".
// The tail is only the real title when the file SHOUTS it — that is how these
// files name a block ("5. Communications protocol — THE CALL PROTOCOL"). For an
// ordinary heading the whole thing is the title, or "Wind — two limits, and the
// low one governs" would print as "Two limits, and the low one governs".
function blockTitle(heading: string): string {
  const full = clean(heading).replace(/^\d+[.)]\s*/, "").trim();
  const tail = (full.split(/\s+[—–]\s+/).pop() ?? full).trim();
  const shouted = tail !== full && tail === tail.toUpperCase() && /[A-Z]{3}/.test(tail);
  let t = shouted ? tail.replace(/^THE\s+/i, "").trim() : full;
  if (t === t.toUpperCase()) t = t.charAt(0) + t.slice(1).toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function parseDate(v: string): string | null {
  const m = v.match(/(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  const iso = v.match(/(\d{4})-(\d{2})-(\d{2})/);
  return iso ? iso[0] : null;
}

// "§5" means nothing to a crew holding the printed MOP — the input-notes file
// says so itself ("they must never refer to §5 … a cross-reference to something
// that is not in front of them is worse than no reference at all"). So every
// §N is swapped for that section's own name, taken from its heading. Nothing is
// invented: the words come out of the document.
function sectionRefMap(secs: Section[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const s of secs) {
    const h = s.heading.match(/^(\d+)\.\s*(.+)$/);
    if (!h) continue;
    const tail = h[2].split(/\s+[—–-]\s+/).pop() ?? h[2];
    let name = clean(tail).trim();
    name = name === name.toUpperCase() ? name.toLowerCase() : name;
    m.set(h[1], /^the\b/i.test(name) ? name : `the ${name}`);
  }
  return m;
}
function dereference(text: string, refs: Map<string, string>): string {
  return text.replace(/§\s*(\d+)/g, (whole, n) => refs.has(n) ? refs.get(n)! : whole);
}

function parseSteps(sec: Section): BackboneStep[] {
  const steps: BackboneStep[] = [];
  let cur: BackboneStep | null = null;
  let sawAction = false;
  // WHO / DO / CHECK / STOP IF — the field-instruction shape the input-notes
  // file uses. The labels are kept in the details verbatim; the first DO item
  // becomes the one-line action when the file has no italic action line.
  let firstDo: string | null = null;
  let inDo = false;
  for (const raw of sec.lines) {
    const l = raw.trim();
    // "**Step 7 — Title**", "### Step 7 — Title", "7. Title", "Step 7: Title"
    const m = l.match(/^(?:\*\*|#+\s*)?step\s*(\d+)\s*[—\-–:.]\s*(.+?)\*{0,2}\s*$/i) ?? l.match(/^\*\*(\d+)[.)]\s*(.+?)\*\*$/);
    if (m) {
      if (cur) { if (!cur.action && firstDo) cur.action = firstDo; steps.push(cur); }
      const title = clean(m[2]).replace(/\*+$/, "").trim();
      cur = { title, action: "", details: "", source: `step ${m[1]} of the document`, hold: /^hold\s*point\b/i.test(title) };
      sawAction = false; firstDo = null; inDo = false;
      continue;
    }
    if (!cur) continue;
    if (!l || /^-{3,}$/.test(l)) continue;
    const label = clean(l).match(/^(WHO|DO|CHECK|STOP IF|WHY[A-Z ]*|FIRST[A-Z —-]*)\s*[:—]\s*(.*)$/);
    if (label) {
      inDo = /^do$/i.test(label[1]);
      if (inDo && label[2].trim()) firstDo = firstDo ?? label[2].trim();
      cur.details = (cur.details ? cur.details + "\n" : "") + clean(l).trim();
      continue;
    }
    if (inDo && !firstDo) {
      const num = clean(l).match(/^\d+[.)]\s*(.+)$/);
      if (num) firstDo = num[1].trim();
    }
    if (!sawAction && /^\*[^*].*\*$/.test(l)) { cur.action = l.slice(1, -1).trim(); sawAction = true; continue; }
    if (/^\*assumption/i.test(l)) { cur.details = (cur.details ? cur.details + "\n" : "") + clean(l).replace(/^\*|\*$/g, "").trim(); continue; }
    cur.details = (cur.details ? cur.details + "\n" : "") + clean(l).trim();
  }
  if (cur) { if (!cur.action && firstDo) cur.action = firstDo; steps.push(cur); }
  return steps.filter(s => s.title);
}

// Two shapes come out of the Projects Claude and both are read here, with no
// AI in the path: the strict BACKBONE template (headings named after the
// builder's own fields) and the INPUT NOTES James actually maintains per job
// ("Willowvale AQ4370-001 — MOP Input Notes v2": numbered sections, a crew
// table, WHO/DO/CHECK/STOP IF steps, don'ts, still-to-confirm).
//
// 23 Sep 2026 — the notes shape used to fail this test, so it was sent to the
// worker instead, which is tuned for lift studies and talk-throughs and came
// back "Nothing extractable found in this document" after a couple of minutes.
// A document written against the builder must never reach the worker.
export function isBackbone(md: string): boolean {
  const head = md.slice(0, 2000).toLowerCase();
  if (head.includes("mop backbone") || head.includes("mop input notes")) return true;
  const secs = splitSections(md);
  const has = (t: (k: string) => boolean) => !!findSec(secs, t);
  if (!has(k => k.includes("method"))) return false;
  return has(k => k.includes("scope"))
      || has(k => k.includes("job facts") || k.includes("job details"))
      || (has(k => /don['’]?ts?\b/.test(k)) && has(k => /still (open|to confirm)|to confirm/.test(k)));
}

export function parseBackbone(md: string): BackboneProposals {
  const secs = splitSections(md);
  const P: BackboneProposals = { backbone: true, crew: [], plant: [], steps: [], blocks: [], replace_steps: false, donts: [], to_confirm: [] };
  const title = secs.find(s => s.level === 1)?.heading;
  if (title) P.title = clean(title);

  // ---- job details -------------------------------------------------------------
  // "JOB DETAILS" (backbone) and "1. Job facts" (input notes) are the same
  // section under two names, so match the word wherever it sits in the heading.
  const job = findSec(secs, k => /\bjob (details|facts)\b/.test(k) || /^job\b/.test(k) || k.includes("§1"));
  if (job) {
    const rows = tableRows(job);
    const kv = new Map<string, string>();
    rows.forEach(r => { if (r.length >= 2) kv.set(r[0].toLowerCase(), r[1]); });
    job.lines.map(clean).forEach(l => { const m = l.match(/^[-*]?\s*([a-z ]+?)\s*[:—-]\s*(.+)$/i); if (m && !kv.has(m[1].toLowerCase().trim())) kv.set(m[1].toLowerCase().trim(), m[2].trim()); });
    // The input-notes shape writes job facts as "**Client chain.** Waveconn >
    // Coretel." — a bolded label ended with a full stop, not a colon.
    job.lines.forEach(raw => {
      const m = raw.trim().match(/^\*\*([A-Z][A-Za-z ,'&]+?)\.?\*\*\s*[:.]?\s*(.+)$/);
      if (!m) return;
      const k = m[1].toLowerCase().trim();
      if (!kv.has(k)) kv.set(k, clean(m[2]).trim());
    });
    for (const [k, v] of kv) {
      if (k.includes("work date") || k === "date") {
        if (isTag(v)) P.to_confirm.push({ note: `Work date: ${v}`, source: "backbone job details" });
        else { const d = parseDate(v); if (d) P.work_date = { value: d, source: `backbone job details: ${v}` }; }
      } else if (k.includes("client")) {
        const chain = (v.match(/^([^—\n(]+?)(?:\s+—|\s+\*|\s+note|\s*\(|$)/i)?.[1] ?? v).trim().replace(/\.$/, "");
        if (chain && !isTag(chain)) P.client_chain = { value: chain, source: `backbone job details: ${v.slice(0, 120)}` };
        if (isTag(v) || /confirm/i.test(v)) P.to_confirm.push({ note: `Client chain: ${v}`, source: "backbone job details" });
      } else if (k.includes("crew")) {
        const line = v.replace(/\[(OPEN|VERIFY)[^\]]*\]/gi, "").replace(/\s+[.·]\s*$/, "").trim();
        if (line) P.crew.push({ name_role: line, source: `backbone job details: ${v.slice(0, 120)}` });
        if (isTag(v)) P.to_confirm.push({ note: `Crew: ${v}`, source: "backbone job details" });
      }
    }

    // Scope, when the document has no "scope of work" heading of its own: the
    // input-notes shape states it as "Structure." and "Work." job facts. Both
    // are printed verbatim, nothing is summarised.
    const structure = kv.get("structure");
    const work = kv.get("work");
    if (work) {
      P.scope = { value: [structure, work].filter(Boolean).join("\n\n"), source: "job facts — “Structure.” and “Work.”" };
    }
    // A file with no "site notes" heading still says what the site is like in
    // its job facts ("All work at ground level at the six guy anchor
    // footings"). An empty SITE CONDITIONS block on a printed MOP reads as
    // though nobody looked, so that line lands there too.
    const noCraneLine = job.lines.map(clean).find(l => /no crane|no lift\b|no climb/i.test(l));
    if (noCraneLine) {
      P.crane_none = { value: true, source: `job facts: “${noCraneLine.trim().slice(0, 120)}”` };
      P.site_notes = {
        value: noCraneLine.replace(/\[(OPEN|VERIFY)[^\]]*\]/gi, "").replace(/\s+/g, " ").trim(),
        source: "job facts",
      };
      tagsIn(noCraneLine).forEach(t => P.to_confirm.push({ note: t, source: "job facts" }));
    }
    tagsIn(job.lines.join("\n")).forEach(t => P.to_confirm.push({ note: t, source: "job facts" }));
  }

  // ---- crew table (input-notes shape: Role | Qty | Call sign | Does) -----------------
  const crewSec = findSec(secs, k => k.includes("crew"));
  if (crewSec && P.crew.length === 0) {
    tableRows(crewSec).forEach(r => {
      if (r.length < 2 || !r[0]) return;
      const qty = r[1] && /^\d+$/.test(r[1]) ? ` ×${r[1]}` : "";
      const call = r[2] ? ` — ${r[2]}` : "";
      P.crew.push({ name_role: `${r[0]}${qty}${call}`.trim(), source: `crew table: ${r.join(" | ").slice(0, 140)}` });
    });
    tagsIn(crewSec.lines.join("\n")).forEach(t => P.to_confirm.push({ note: t, source: "crew table" }));
  }

  // ---- crane & plant -------------------------------------------------------------
  const craneHead = findSec(secs, k => k.includes("crane") || k.includes("plant") || k.includes("§2"));
  // Same merge as the method: the input-notes shape puts the rigging table and
  // the instruments table under child headings of "Plant, rigging and
  // instruments", so reading only the parent found no plant lines at all.
  const crane = craneHead ? mergeChildren(secs, craneHead) : undefined;
  if (crane) {
    const text = crane.lines.map(clean).join("\n");
    if (/no crane/i.test(text)) P.crane_none = { value: true, source: "backbone crane & plant: “No crane on this MOP”" };
    tableRows(crane).forEach(r => {
      if (r.length >= 2 && r[0] && !/^item$/i.test(r[0])) {
        P.plant.push({ item: r[0], details: r.slice(1).filter(Boolean).join(" · "), source: `backbone plant table: ${r[0]}` });
        tagsIn(r.join(" ")).forEach(t => P.to_confirm.push({ note: `${r[0]}: ${t}`, source: "backbone plant table" }));
      }
    });
  }

  // ---- scope ---------------------------------------------------------------------
  const scope = findSec(secs, k => k.includes("scope of work") || k.startsWith("scope"));
  if (scope) {
    const v = proseOf(scope);
    if (v) P.scope = { value: v, source: `backbone “${scope.heading}”` };
  }

  // ---- standing controls -----------------------------------------------------------
  const ctl = findSec(secs, k => k.includes("standing control"));
  if (ctl) {
    const decide = (row: string): boolean | null => {
      const r = row.toLowerCase();
      if (/untick|\boff\b|\bno\b|leave unticked|not ticked/.test(r)) return false;
      if (/\btick\b|\bon\b|\byes\b|ticked/.test(r)) return true;
      return null;
    };
    const rows = tableRows(ctl).map(r => r.join(" | "));
    const lines = rows.length ? rows : ctl.lines.map(clean);
    for (const row of lines) {
      const l = row.toLowerCase();
      const setTo = row.split("|")[1] ?? row;
      if (l.includes("suspended")) { const d = decide(setTo); if (d != null) P.suspended_loads = { value: d, source: `backbone standing controls: ${row.slice(0, 140)}` }; }
      if (l.includes("painted")) { const d = decide(setTo); if (d != null) P.painted_structure = { value: d, source: `backbone standing controls: ${row.slice(0, 140)}` }; }
    }
  }

  // ---- site notes ------------------------------------------------------------------
  const site = findSec(secs, k => k.includes("site notes") || k.includes("site conditions"));
  if (site) {
    const v = proseOf(site);
    if (v) P.site_notes = { value: v, source: `backbone “${site.heading}”` };
    tagsIn(v).forEach(t => P.to_confirm.push({ note: t, source: "backbone site notes" }));
  }

  // ---- wind -----------------------------------------------------------------------
  const wind = findSec(secs, k => k.includes("wind"));
  if (wind) {
    const text = wind.lines.map(clean).join("\n");
    // 23 Sep 2026 — an [OPEN] SAFETY limit used to swallow the whole section,
    // so a file that plainly stated the working limit ("Above 8 m/s — do not
    // set or certify tension") still printed WIND LIMIT — NOT SET. The limit
    // that stops the work is the one the MOP needs; the open item is recorded
    // as well, not instead of it.
    const stopLine = wind.lines.map(clean).find(l =>
      /m\/s/i.test(l) && /(do not|don.t|never|stop|cease|not set or certify)/i.test(l));
    const m = stopLine?.match(/(\d+(?:\.\d+)?)\s*m\/s/i)
           ?? (isTag(text) ? null : text.match(/(\d+(?:\.\d+)?)\s*m\/s/i));
    if (m) P.wind_limit_ms = { value: Number(m[1]), source: (stopLine ?? text).replace(/\s*\|\s*/g, " ").trim().slice(0, 160) };
    if (isTag(text)) P.to_confirm.push({ note: `Wind limit: ${tagsIn(text).join("; ")}`, source: `\u201c${clean(wind.heading)}\u201d in the document` });
  }

  // ---- environmental notes ------------------------------------------------------------
  const env = findSec(secs, k => k.includes("environmental"));
  if (env) {
    const v = proseOf(env);
    if (v) P.environmental_notes = { value: v, source: `backbone “${env.heading}”` };
    tagsIn(v).forEach(t => P.to_confirm.push({ note: t, source: "backbone environmental notes" }));
  }

  // ---- method ---------------------------------------------------------------------
  // 23 Sep 2026 — the method is found by finding STEPS, never by finding a
  // heading. The real Willowvale notes carry "### Method approval" inside the
  // acceptance section, which has more text in it than "## 6. The method"
  // (whose own body is empty, because its steps sit under a "### Order of work"
  // child). findSec therefore picked the approval note, the reader found no
  // steps in it, and the MOP came out with nothing but the seed step. Scanning
  // the whole document for step headers cannot be fooled by what a heading is
  // called — every step in this shape starts its own line as "**STEP n — …**".
  const wholeDoc: Section = { level: 0, heading: "", key: "", lines: md.split(/\r?\n/) };
  const allSteps = parseSteps(wholeDoc);
  const method = findSec(secs, k => /\bmethod\b/.test(k) && !/approval|transfer|statement/.test(k))
              ?? findSec(secs, k => k.includes("order of work"));
  if (allSteps.length > 0 || method) {
    P.steps = allSteps;
    // steps may sit under the method heading or under "### Step n" children —
    // take the method section plus every following section until a non-step heading
    // Everything under the method heading belongs to the method, whatever the
    // sub-headings are called. The input-notes shape puts its steps under a
    // "### Order of work" child, so a merge that only accepted "### Step n"
    // found no steps at all and the method came back empty.


    // Projects brief 2, A2 (23 Sep 2026): blocks that govern every step without
    // being one. The document names them and says they must print as their own
    // section before the method — the call protocol is the case that found it.
    // They used to be dropped because the builder had no field for them.
    for (const sec of secs) {
      // The wind rule and the guy schedule are the other two things this job
      // cannot be run without: the wind table decides whether tension can be
      // set at all, and the schedule is what every reading is recorded against
      // ("Print this table in the MOP" — the file's own instruction).
      if (!/call protocol|communications protocol|the temporary rig|wind|the \d+ guys|guy schedule/.test(sec.key)) continue;
      const body = blockBodyOf(sec);
      const title = blockTitle(sec.heading);
      if (!body || P.blocks.some(b => blockKey(b.title) === blockKey(title))) continue;
      P.blocks.push({ title, body, source: `“${clean(sec.heading)}” in the document` });
    }

    // "§5" is meaningless on a printed MOP — swap every section reference for
    // that section's own name, out of the document's own headings.
    const refs = sectionRefMap(secs);
    P.steps = P.steps.map(s => ({
      ...s,
      action: dereference(s.action, refs),
      details: dereference(s.details, refs),
    }));
    P.blocks = P.blocks.map(b => ({ ...b, body: dereference(b.body, refs) }));

    // A document that carries its own numbered method replaces what is on the
    // MOP rather than adding to it — re-extracting the same file was doubling
    // every step (James, 23 Sep). The manager can still untick it.
    P.replace_steps = (!!method && /replace/i.test(method.heading))
      || (!!method && /replace (all|the) .*steps/i.test(method.lines.join(" ")))
      || P.steps.length >= 3;
    P.steps.forEach(s => tagsIn(`${s.action} ${s.details}`).forEach(t => P.to_confirm.push({ note: `${s.title}: ${t}`, source: s.source })));
  }

  // ---- don'ts --------------------------------------------------------------------------
  const donts = findSec(secs, k => /don['’]?ts?\b/.test(k) || k.includes("never"));
  if (donts) bullets(donts).forEach(b => P.donts.push({ text: b, source: `backbone “${donts.heading}”` }));

  // ---- still open ------------------------------------------------------------------------
  for (const s of secs) {
    if (!(/still open|to confirm|open items|blockers|before you generate/.test(s.key))) continue;
    tableRows(s).forEach(r => {
      const note = r.find(c => c && !/^\d+$/.test(c) && !/^~~/.test(c));
      if (note && !/closed/i.test(note)) P.to_confirm.push({ note: clean(note), source: `backbone “${s.heading}”` });
    });
    bullets(s).forEach(b => P.to_confirm.push({ note: b, source: `backbone “${s.heading}”` }));
  }
  // dedupe
  const seen = new Set<string>();
  P.to_confirm = P.to_confirm
    .filter(t => !/^open\.?$/i.test(t.note.trim()))   // a bare [OPEN] tag says nothing on its own
    .filter(t => { const k = t.note.toLowerCase().slice(0, 80); if (seen.has(k)) return false; seen.add(k); return true; });

  return P;
}
